/**
 * Campi di form del portale acquisti.
 *
 * Sono qui e non in @shared perche' hanno una convenzione che i componenti di manutenzioni non
 * hanno: **stringa vuota significa «non valorizzato»**, e il backend acquisti la traduce in null
 * (`costruisciPayload`: `body[chiave] === "" ? null : ...`). Serve a distinguere «zero giorni di
 * preavviso» da «preavviso non censito», che su 29 contratti con 3 preavvisi valorizzati e' la
 * distinzione piu' importante che ci sia in queste pagine.
 */

import React from "react";

export function Campo({ label, aiuto, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="label">{label}</span>
      {children}
      {aiuto && (
        <span className="block text-xs text-gray-500 mt-1">{aiuto}</span>
      )}
    </label>
  );
}

export function Testo({ valore, onChange, ...rest }) {
  return (
    <input
      type="text"
      className="input"
      value={valore ?? ""}
      onChange={(e) => onChange(e.target.value)}
      {...rest}
    />
  );
}

export function AreaTesto({ valore, onChange, righe = 3, ...rest }) {
  return (
    <textarea
      className="input"
      rows={righe}
      value={valore ?? ""}
      onChange={(e) => onChange(e.target.value)}
      {...rest}
    />
  );
}

export function Data({ valore, onChange, ...rest }) {
  return (
    <input
      type="date"
      className="input"
      value={valore ?? ""}
      onChange={(e) => onChange(e.target.value)}
      {...rest}
    />
  );
}

/** `""` esce come `""`, non come 0: vedi la nota in testa al file. */
export function Numero({ valore, onChange, ...rest }) {
  return (
    <input
      type="number"
      className="input"
      value={valore ?? ""}
      onChange={(e) => onChange(e.target.value)}
      {...rest}
    />
  );
}

export function Scelta({ valore, onChange, opzioni, vuoto = "—", ...rest }) {
  return (
    <select
      className="input"
      value={valore ?? ""}
      onChange={(e) => onChange(e.target.value)}
      {...rest}
    >
      <option value="">{vuoto}</option>
      {opzioni.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function Interruttore({ valore, onChange, testo }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm text-gray-700">
      <input
        type="checkbox"
        className="rounded border-gray-300 text-acquisti-600 focus:ring-acquisti-500"
        checked={!!valore}
        onChange={(e) => onChange(e.target.checked)}
      />
      {testo}
    </label>
  );
}

export function Avviso({ tipo = "info", children }) {
  const stili = {
    info: "bg-blue-50 border-blue-200 text-blue-900",
    attenzione: "bg-yellow-50 border-yellow-200 text-yellow-900",
    errore: "bg-red-50 border-red-200 text-red-900",
    ok: "bg-green-50 border-green-200 text-green-900",
  };
  return (
    <div className={`rounded-md border px-4 py-3 text-sm ${stili[tipo]}`}>
      {children}
    </div>
  );
}

export function Caricamento({ testo = "Caricamento…" }) {
  return (
    <div className="flex items-center gap-3 text-sm text-gray-500 py-8">
      <div className="spinner w-5 h-5" />
      {testo}
    </div>
  );
}

export function Vuoto({ titolo, children }) {
  return (
    <div className="text-center py-12 text-gray-500">
      <p className="font-medium text-gray-700">{titolo}</p>
      {children && <p className="text-sm mt-1">{children}</p>}
    </div>
  );
}

/**
 * Scelta di piu' valori in cui **il primo e' il principale** (tipologie di un capitolato o di un
 * contratto, ADR-0006): il backend porta il lookup singolo sul primo dell'elenco. L'ordine e' quello
 * dei clic; «rendi principale» sposta in testa.
 */
export function SceltaMultipla({ valori, onChange, opzioni, disabled = false }) {
  const scelti = valori || [];
  const etichetta = (id) => opzioni.find((o) => o.value === id)?.label || id;
  const alterna = (id) =>
    onChange(
      scelti.includes(id) ? scelti.filter((x) => x !== id) : [...scelti, id],
    );
  const principale = (id) => onChange([id, ...scelti.filter((x) => x !== id)]);

  return (
    <div className="space-y-2">
      {scelti.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {scelti.map((id, i) => (
            <span
              key={id}
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${
                i === 0
                  ? "border-acquisti-600 bg-acquisti-50 text-acquisti-800 font-medium"
                  : "border-gray-300 text-gray-700"
              }`}
            >
              {etichetta(id)}
              {i === 0 ? (
                <span className="text-[10px] uppercase">principale</span>
              ) : (
                !disabled && (
                  <button
                    type="button"
                    className="text-[10px] text-acquisti-700 underline"
                    onClick={() => principale(id)}
                  >
                    rendi principale
                  </button>
                )
              )}
            </span>
          ))}
        </div>
      )}
      {!disabled && (
        <div className="max-h-48 overflow-y-auto rounded-md border border-gray-200 p-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
          {opzioni.map((o) => (
            <label
              key={o.value}
              className="inline-flex items-center gap-2 text-sm text-gray-700"
            >
              <input
                type="checkbox"
                className="rounded border-gray-300 text-acquisti-600 focus:ring-acquisti-500"
                checked={scelti.includes(o.value)}
                onChange={() => alterna(o.value)}
              />
              {o.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
